<template>
  <Transition name="fade">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[9998] bg-black/30 backdrop-blur-[1px]"
      @click.self="closeDrawer"
    />
  </Transition>

  <Transition name="slide">
    <aside
      v-if="modelValue"
      class="fixed right-0 top-0 z-[9999] h-screen w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
    >
      <div
        class="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p
              class="text-xs font-semibold uppercase tracking-[0.2em] text-[#2f8f2f]"
            >
              Layout do relatório
            </p>

            <h2 class="mt-1 text-2xl font-bold text-gray-900">
              {{ isEdit ? "Editar layout" : "Novo layout" }}
            </h2>

            <p class="mt-1 text-sm text-gray-500">
              Configure a base visual que será aplicada aos relatórios técnicos.
            </p>
          </div>

          <button
            type="button"
            class="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            @click="closeDrawer"
          >
            <PhX class="h-5 w-5" />
          </button>
        </div>
      </div>

      <form class="px-6 py-6" @submit.prevent="submitForm">
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Nome do layout <span class="text-red-500">*</span>
            </label>

            <input
              v-model="form.name"
              type="text"
              class="report-input"
              placeholder="Ex: Layout Padrão X Solution"
              required
            />
          </div>

          <div class="md:col-span-2">
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Descrição
            </label>

            <textarea
              v-model="form.description"
              rows="4"
              class="report-input resize-none"
              placeholder="Descreva quando este layout deve ser usado..."
            />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Formato da página
            </label>

            <select v-model="form.page_format" class="report-input">
              <option value="a4">A4</option>
              <option value="letter">Carta</option>
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Orientação
            </label>

            <select v-model="form.orientation" class="report-input">
              <option value="portrait">Retrato</option>
              <option value="landscape">Paisagem</option>
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Cor principal
            </label>

            <div class="flex gap-2">
              <input
                v-model="form.primary_color"
                type="color"
                class="h-11 w-14 cursor-pointer rounded-xl border border-gray-200 bg-white p-1"
              />

              <input
                v-model="form.primary_color"
                type="text"
                class="report-input"
                placeholder="#2f8f2f"
              />
            </div>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-gray-700">
              Cor secundária
            </label>

            <div class="flex gap-2">
              <input
                v-model="form.secondary_color"
                type="color"
                class="h-11 w-14 cursor-pointer rounded-xl border border-gray-200 bg-white p-1"
              />

              <input
                v-model="form.secondary_color"
                type="text"
                class="report-input"
                placeholder="#5dbb45"
              />
            </div>
          </div>
        </div>

        <div class="mt-8">
          <div class="mb-4">
            <h3 class="text-base font-bold text-gray-800">Arquivos visuais</h3>

            <p class="mt-1 text-sm text-gray-500">
              Envie os arquivos que formarão a identidade visual do relatório.
            </p>
          </div>

          <div class="space-y-4">
            <ReportLayoutAssetUploader
              title="Capa do relatório"
              description="Imagem usada como primeira página ou capa visual do relatório."
              :preview="assetPreviews.cover"
              :icon="PhImageSquare"
              @change="handleAssetChange('cover', $event)"
              @remove="removeAsset('cover')"
            />

            <ReportLayoutAssetUploader
              title="Cabeçalho / timbre"
              description="Imagem aplicada no topo das páginas internas do relatório."
              :preview="assetPreviews.header"
              :icon="PhLayout"
              @change="handleAssetChange('header', $event)"
              @remove="removeAsset('header')"
            />

            <ReportLayoutAssetUploader
              title="Rodapé"
              description="Imagem aplicada no final das páginas internas do relatório."
              :preview="assetPreviews.footer"
              :icon="PhLayout"
              @change="handleAssetChange('footer', $event)"
              @remove="removeAsset('footer')"
            />

            <ReportLayoutAssetUploader
              title="Marca d’água"
              description="Imagem sutil aplicada ao fundo das páginas, quando necessário."
              :preview="assetPreviews.watermark"
              :icon="PhStamp"
              @change="handleAssetChange('watermark', $event)"
              @remove="removeAsset('watermark')"
            />
          </div>
        </div>

        <!-- <div
          class="mt-6 rounded-2xl border border-green-100 bg-green-50/60 p-4"
        >
          <div class="flex gap-3">
            <div
              class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#2f8f2f] shadow-sm"
            >
              <PhImageSquare class="h-5 w-5" />
            </div>

            <div>
              <h3 class="text-sm font-bold text-gray-800">Arquivos visuais</h3>

              <p class="mt-1 text-sm text-gray-600">
                O upload de capa, cabeçalho, rodapé e marca d’água será
                conectado no próximo bloco. Esta etapa cria a base do layout e
                suas configurações principais.
              </p>
            </div>
          </div>
        </div> -->

        <div class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label
            class="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4"
          >
            <div>
              <p class="text-sm font-semibold text-gray-800">
                Definir como padrão
              </p>

              <p class="mt-0.5 text-xs text-gray-500">
                Usado automaticamente em novos relatórios.
              </p>
            </div>

            <input
              v-model="form.is_default"
              type="checkbox"
              class="h-5 w-5 rounded border-gray-300 text-[#2f8f2f] focus:ring-green-100"
            />
          </label>

          <label
            class="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-4"
          >
            <div>
              <p class="text-sm font-semibold text-gray-800">Layout ativo</p>

              <p class="mt-0.5 text-xs text-gray-500">
                Disponível para uso nos relatórios.
              </p>
            </div>

            <input
              v-model="form.is_active"
              type="checkbox"
              class="h-5 w-5 rounded border-gray-300 text-[#2f8f2f] focus:ring-green-100"
            />
          </label>
        </div>

        <div
          class="sticky bottom-0 mt-8 flex items-center justify-end gap-3 border-t border-gray-100 bg-white py-5"
        >
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            @click="closeDrawer"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#2f8f2f] to-[#5dbb45] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="loading"
          >
            <PhCircleNotch v-if="loading" class="h-5 w-5 animate-spin" />
            <PhCheck v-else class="h-5 w-5" />

            {{ isEdit ? "Salvar alterações" : "Cadastrar layout" }}
          </button>
        </div>
      </form>
    </aside>
  </Transition>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";
import {
  PhCheck,
  PhCircleNotch,
  PhFileImage,
  PhImageSquare,
  PhLayout,
  PhStamp,
  PhX,
} from "@phosphor-icons/vue";

import ReportLayoutAssetUploader from "./ReportLayoutAssetUploader.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "submit", "close"]);

const form = reactive(getDefaultForm());

const assetFiles = reactive({
  cover: null,
  header: null,
  footer: null,
  watermark: null,
});

const assetPreviews = reactive({
  cover: "",
  header: "",
  footer: "",
  watermark: "",
});

const isEdit = computed(() => Boolean(form.id));

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;

    fillForm(props.layout);
  }
);

watch(
  () => props.layout,
  (layout) => {
    if (props.modelValue) {
      fillForm(layout);
    }
  }
);

function getDefaultForm() {
  return {
    id: null,
    name: "",
    description: "",
    cover_path: "",
    cover_url: "",
    header_path: "",
    header_url: "",
    footer_path: "",
    footer_url: "",
    watermark_path: "",
    watermark_url: "",
    primary_color: "#2f8f2f",
    secondary_color: "#5dbb45",
    page_format: "a4",
    orientation: "portrait",
    logo_area: null,
    client_name_area: null,
    client_document_area: null,
    settings: null,
    is_default: false,
    is_active: true,
  };
}

function fillForm(layout = null) {
  Object.assign(form, getDefaultForm());

  resetAssetFiles();
  resetAssetPreviews();

  if (!layout) return;

  Object.assign(form, {
    ...getDefaultForm(),
    ...layout,
    primary_color: layout.primary_color || "#2f8f2f",
    secondary_color: layout.secondary_color || "#5dbb45",
    page_format: layout.page_format || "a4",
    orientation: layout.orientation || "portrait",
    is_default: Boolean(layout.is_default),
    is_active: Boolean(layout.is_active),
  });

  assetPreviews.cover = layout.cover_url || "";
  assetPreviews.header = layout.header_url || "";
  assetPreviews.footer = layout.footer_url || "";
  assetPreviews.watermark = layout.watermark_url || "";
}

function submitForm() {
  emit("submit", {
    id: form.id,
    isEdit: isEdit.value,
    assetFiles: {
      cover: assetFiles.cover,
      header: assetFiles.header,
      footer: assetFiles.footer,
      watermark: assetFiles.watermark,
    },
    assetPreviews: {
      cover: assetPreviews.cover,
      header: assetPreviews.header,
      footer: assetPreviews.footer,
      watermark: assetPreviews.watermark,
    },
    payload: {
      name: form.name?.trim(),
      description: form.description?.trim(),

      cover_path: form.cover_path || null,
      cover_url: form.cover_url || null,

      header_path: form.header_path || null,
      header_url: form.header_url || null,

      footer_path: form.footer_path || null,
      footer_url: form.footer_url || null,

      watermark_path: form.watermark_path || null,
      watermark_url: form.watermark_url || null,

      primary_color: form.primary_color || null,
      secondary_color: form.secondary_color || null,

      page_format: form.page_format,
      orientation: form.orientation,

      logo_area: form.logo_area,
      client_name_area: form.client_name_area,
      client_document_area: form.client_document_area,
      settings: form.settings,

      is_default: Boolean(form.is_default),
      is_active: Boolean(form.is_active),
    },
  });
}

function closeDrawer() {
  emit("update:modelValue", false);
  emit("close");
}

function handleAssetChange(type, file) {
  if (!assetFiles.hasOwnProperty(type)) return;

  assetFiles[type] = file;

  if (assetPreviews[type] && assetPreviews[type].startsWith("blob:")) {
    URL.revokeObjectURL(assetPreviews[type]);
  }

  assetPreviews[type] = URL.createObjectURL(file);
}

function removeAsset(type) {
  if (!assetFiles.hasOwnProperty(type)) return;

  if (assetPreviews[type] && assetPreviews[type].startsWith("blob:")) {
    URL.revokeObjectURL(assetPreviews[type]);
  }

  assetFiles[type] = null;
  assetPreviews[type] = "";

  form[`${type}_path`] = "";
  form[`${type}_url`] = "";
}

function resetAssetFiles() {
  Object.keys(assetFiles).forEach((key) => {
    assetFiles[key] = null;
  });
}

function resetAssetPreviews() {
  Object.keys(assetPreviews).forEach((key) => {
    if (assetPreviews[key] && assetPreviews[key].startsWith("blob:")) {
      URL.revokeObjectURL(assetPreviews[key]);
    }

    assetPreviews[key] = "";
  });
}
</script>

<style scoped>
.report-input {
  @apply w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none transition placeholder:text-gray-300 focus:border-[#2f8f2f] focus:ring-2 focus:ring-green-100;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
